import { useState, useEffect} from "react";
import "./ViewPage.css";
import "./DeleteOrders.css";
import "./EditPage.css";
import { FiEdit2, FiX } from "react-icons/fi";
import { authFetch } from "./authFetch";

const PAGE_SIZE = 10;
const MAX_CLIENT_RESULTS = 1000;

const STATUS_COLORS = {
  "In-Process": "#99dff7ff",
  Pending: "#f3c574ff",
  Shipped: "#e0dd3eff",
  "Out for Delivery": "#cbb8ebff",
  Delivered: "#86efac",
  Cancelled: "#fca5a5ff",
};

const LETTERS = [
  "#","A","B","C","D","E","F","G","H","I","J","K","L",
  "M","N","O","P","Q","R","S","T","U","V","W","X","Y","Z"
];

/* STRICT DATE REGEX: MM/DD/YYYY HH:MM:SS AM */
const DATE_REGEX =
  /^([1-9]|0[1-9]|1[0-2])\/([1-9]|0[1-9]|[12]\d|3[01])\/\d{4}\s([1-9]|0[1-9]|1[0-2]):[0-5]\d:[0-5]\d\s(AM|PM)$/;

// F-02/03: Date-only regex for Order Received field
const DATE_ONLY_REGEX = /^([1-9]|0[1-9]|1[0-2])\/([1-9]|0[1-9]|[12]\d|3[01])\/\d{4}$/;


export default function EditPage() {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // Search state
  const [clinic, setClinic] = useState("");
  const [patient, setPatient] = useState("");
  const [rawResults, setRawResults] = useState([]);

  const [statusFilter, setStatusFilter] = useState("ALL");
  const [sortOrder, setSortOrder] = useState("NEWEST");
  const [physicianFilter, setPhysicianFilter] = useState("ALL"); // F-04
  const [currentPage, setCurrentPage] = useState(1);

  const [openTracking, setOpenTracking] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  // EDIT MODAL
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [orderToEdit, setOrderToEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  // Edit form
  const [editStatus, setEditStatus] = useState("");
  const [editOrderReceived, setEditOrderReceived] = useState(""); // F-02/03: editShip → editOrderReceived
  // F-12: const [editDelivery, setEditDelivery] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editPhysician, setEditPhysician] = useState(""); // F-04
  const [trackingRows, setTrackingRows] = useState([]);

  // Field-specific validation errors
  const [orderReceivedError, setOrderReceivedError] = useState(""); // F-02/03: shipError → orderReceivedError
  // F-12: const [deliveryError, setDeliveryError] = useState("");

  // -------------------------------
  // DATE VALIDATION UTIL
  // -------------------------------
  function isValidDateFormat(v) {
    return DATE_REGEX.test(v.trim());
  }

  // F-02/03: validate date-only format for Order Received, no future dates
  function isValidDateOnly(v) {
    if (!DATE_ONLY_REGEX.test(v.trim())) return false;
    const parts = v.trim().split("/");
    const entered = new Date(parts[2], parts[0] - 1, parts[1]);
    return entered <= new Date();
  }

  // -------------------------------
  // VALIDATION ENGINE
  // -------------------------------
  function validateFields(status, ship, delivery) {
    ship = ship.trim();
    delivery = delivery.trim();

    let shipErr = "";
    let delErr = "";

    // Cancelled → no dates allowed
    if (status === "Cancelled") {
      return { shipErr: "", delErr: "" };
    }

    // Pending / In-Process → delivery NOT allowed
    if (status === "Pending" || status === "In-Process") {
      if (delivery) {
        delErr = "Delivery date is not allowed for this status.";
      }
      if (ship && !isValidDateFormat(ship))
        shipErr = "Invalid format. Use M/D/YYYY HH:MM:SS AM/PM";
      return { shipErr, delErr };
    }

    // Shipped → Ship required + valid
    if (status === "Shipped") {
      if (!ship) shipErr = "Shipped orders require a Ship Date.";
      else if (!isValidDateFormat(ship))
        shipErr = "Invalid format. Use M/D/YYYY HH:MM:SS AM/PM";

      if (delivery && !isValidDateFormat(delivery))
        delErr = "Invalid format. Use M/D/YYYY HH:MM:SS AM/PM";

      return { shipErr, delErr };
    }

    // Delivered → both required
    if (status === "Delivered") {
      if (!ship) shipErr = "Delivered orders require a Ship Date.";
      else if (!isValidDateFormat(ship))
        shipErr = "Invalid format. Use M/D/YYYY HH:MM:SS AM/PM";

      if (!delivery)
        delErr = "Delivered orders require a Delivery Date.";
      else if (!isValidDateFormat(delivery))
        delErr = "Invalid format. Use M/D/YYYY HH:MM:SS AM/PM";

      return { shipErr, delErr };
    }

    // Out for Delivery → BOTH ship + delivery REQUIRED
if (status === "Out for Delivery") {

  // Ship Date required + must be valid
  if (!ship)
    shipErr = "Ship date required.";
  else if (!isValidDateFormat(ship))
    shipErr = "Invalid format. Use M/D/YYYY HH:MM:SS AM/PM";

  // Delivery Date required + must be valid
  if (!delivery)
    delErr = "Delivery date required.";
  else if (!isValidDateFormat(delivery))
    delErr = "Invalid format. Use M/D/YYYY HH:MM:SS AM/PM";

  return { shipErr, delErr };
}

return { shipErr, delErr };

  }

  // -------------------------------
  // SAVE DISABLED?
  // -------------------------------
 // RUN VALIDATION & SET ERRORS IN STATE
// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  if (!orderToEdit) {
    setOrderReceivedError("");
    // F-12: setDeliveryError("");
    return;
  }

  // F-02/03: use date-only validation for Order Received
  const err = editOrderReceived.trim() && !isValidDateOnly(editOrderReceived) 
    ? (DATE_ONLY_REGEX.test(editOrderReceived.trim()) ? "Order Received date cannot be in the future." : "Invalid format. Use M/D/YYYY")
    : "";
  setOrderReceivedError(err);
  // F-12: setDeliveryError(delErr);
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [editStatus, editOrderReceived, orderToEdit]); // F-02/03: editShip → editOrderReceived


// Save disabled based on error state
const saveDisabled = !!orderReceivedError; // F-02/03: shipError → orderReceivedError


  // -------------------------------
  // OPEN EDIT MODAL
  // -------------------------------
  function openEditModal(order) {
    setOrderToEdit(order);
    setEditStatus(order.status || "");
    setEditOrderReceived(order.order_received || ""); // F-02/03: shipDate → order_received
    // F-12: setEditDelivery(order.deliveryDate || "");
    setEditNotes(order.comments || "");
    setEditPhysician(order.physician || ""); // F-04
    setTrackingRows(order.trackingLinks || []);

    setOrderReceivedError("");
    // F-12: setDeliveryError("");

    setEditModalOpen(true);
  }

  function closeEditModal() {
    if (saving) return;
    setEditModalOpen(false);
    setOrderToEdit(null);
    setOrderReceivedError("");
    // F-12: setDeliveryError("");
  }

  // -------------------------------
  // STATUS CHANGE LOGIC
  // -------------------------------
  function handleStatusChange(newStatus) {
    setEditStatus(newStatus);

    if (newStatus === "Cancelled") {
      setEditOrderReceived(""); // F-02/03
      // F-12: setEditDelivery("");
      setTrackingRows([]);
    }

    if (newStatus === "Pending" || newStatus === "In-Process") {
      // F-12: setEditDelivery("");
    }
  }

  // -------------------------------
  // TRACKING HANDLERS
  // -------------------------------
  function updateTracking(index, value) {
    setTrackingRows(prev => {
      const arr = [...prev];
      arr[index] = value;
      return arr;
    });
  }

  function addTrackingRow() {
    setTrackingRows(prev => [...prev, ""]);
  }

  function removeTrackingRow(index) {
    setTrackingRows(prev => prev.filter((_, i) => i !== index));
  }

  // -------------------------------
  // SAVE ORDER
  // -------------------------------
  async function saveEdit() {
    if (!orderToEdit || saveDisabled) return;

    const payload = {
      comments: editNotes,
      status: editStatus,
      // F-02/03: shipDate replaced by order_received
      order_received: editStatus === "Cancelled" ? "" : editOrderReceived.trim(),
      // F-12: deliveryDate removed
      deliveryDate: "",
      physician: editPhysician.trim(), // F-04

      trackingLinks:
        editStatus === "Cancelled"
          ? []
          : trackingRows.filter(v => v.trim())
    };

    setSaving(true);
    try {
      const res = await authFetch(
        `${API_BASE}/admin/edit-order/${orderToEdit.orderId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        }
      );

      const data = await res.json();
      if (!res.ok || !data.updated) throw new Error();

      // update UI instantly
      setRawResults(prev =>
        prev.map(o =>
          o.orderId === orderToEdit.orderId ? { ...o, ...payload } : o
        )
      );

      closeEditModal();
    } catch {
      alert("Failed to update order.");
    } finally {
      setSaving(false);
    }
  }

  // -------------------------------
  // LOAD CLINICS
  // -------------------------------
  const [allClinics, setAllClinics] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLetter, setSelectedLetter] = useState("");
  const [letterClinics, setLetterClinics] = useState([]);

  useEffect(() => {
    async function loadClinics() {
      try {
        const res = await authFetch(`${API_BASE}/admin/clinics`);
        const data = await res.json();
        const names = (data.items || []).map(i => i.clinicName);
        names.sort((a, b) => a.localeCompare(b));
        setAllClinics(names);
      } catch {}
    }
    loadClinics();
  }, [API_BASE]);

  function handleLetterClick(letter) {
    setSelectedLetter(letter);

    let list = [];
    if (letter === "#") {
      list = allClinics.filter(c => !/^[A-Za-z]/.test(c));
    } else {
      list = allClinics.filter(c => c.toUpperCase().startsWith(letter));
    }

    setLetterClinics(list);
    setModalOpen(true);
  }

  // -------------------------------
  // SEARCH ORDERS
  // -------------------------------
  async function search() {
    if (clinic.trim().length < 3) {
      setError("Clinic name must be at least 3 characters.");
      return;
    }

    setError("");
    setLoading(true);
    setHasSearched(false);
    setOpenTracking({});
    setCurrentPage(1);
    // F-04: reset filters on every new search so stale selections don't hide results
    setStatusFilter("ALL");
    setPhysicianFilter("ALL");

    try {
      const params = new URLSearchParams();
      params.append("clinic", clinic.trim());
      if (patient.trim()) params.append("patient", patient.trim());

      const res = await authFetch(`${API_BASE}/admin/orders?${params}`);
      const data = await res.json();

      const results = (data.results || []).slice(0, MAX_CLIENT_RESULTS);
      setRawResults(results);
      setHasSearched(true);
    } catch {
      setError("Unable to fetch orders.");
      setRawResults([]);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  }

  // -------------------------------
  // FILTER + SORT + PAGINATION
  // -------------------------------
  let filtered = rawResults;

  if (patient.trim()) {
    const p = patient.toLowerCase();
    filtered = filtered.filter(o => o.name?.toLowerCase().includes(p));
  }

  if (statusFilter !== "ALL") {
    filtered = filtered.filter(o => o.status === statusFilter);
  }

  // F-04: physician filter
  if (physicianFilter !== "ALL") {
    filtered = filtered.filter(o => o.physician === physicianFilter);
  }

  filtered = [...filtered].sort((a, b) => {
    const da = new Date(a.order_received || 0); // F-02/03: order_received
    const db = new Date(b.order_received || 0); // F-02/03: order_received
    return sortOrder === "NEWEST" ? db - da : da - db;
  });

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [patient, statusFilter, sortOrder, physicianFilter]); // F-04

  const display = v => (!v || v.trim() === "" ? "N/A" : v);
  // F-11: strip timestamp from date fields
  const displayDate = (v) => {
    if (!v || v.trim() === "") return "N/A";
    return v.split(" ")[0];
  };

  // ------------------------------------------------
  // RENDER
  // ------------------------------------------------
  return (
    <>
      {/* EDIT MODAL */}
      {editModalOpen && (
        <div className="modal-overlay">
          <div className="edit-modal-container">

            <div className="modal-header">
              <span>Edit Order</span>
              <button className="modal-close-btn" onClick={closeEditModal}>×</button>
            </div>

            <div className="edit-modal-body edit-form">

              <div className="edit-grid">

                {/* LEFT COLUMN */}
                <div>
                  <label>Status</label>
                  <select
                    value={editStatus}
                    onChange={(e) => handleStatusChange(e.target.value)}
                  >
                    <option>In-Process</option>
                    <option>Pending</option>
                    <option>Shipped</option>
                    <option>Out for Delivery</option>
                    <option>Delivered</option>
                    <option>Cancelled</option>
                  </select>

                  {/* SHIP DATE - F-02/03: commented out, replaced by Order Received
                  <label>Shipped Date</label>
                  <input
                    value={editShip}
                    disabled={editStatus === "Cancelled"}
                    onChange={...}
                    placeholder="M/D/YYYY HH:MM:SS AM/PM"
                  />
                  */}
                  <label>Order Received</label>
                  <input
                    value={editOrderReceived}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEditOrderReceived(v);
                      // F-02/03: validate date-only format
                      const err = v.trim() && !isValidDateOnly(v) ? "Invalid format. Use M/D/YYYY" : "";
                      setOrderReceivedError(err);
                    }}
                    placeholder="M/D/YYYY"
                  />
                  {orderReceivedError && (
                    <div className="validation-error">{orderReceivedError}</div>
                  )}
                </div>

                {/* RIGHT COLUMN */}
                <div>
                  {/* F-12: Delivery Date input removed
                  <label>Delivery Date</label>
                  <input value={editDelivery} ... />
                  */}

                  {/* F-04: Physician field (editable per Trey) */}
                  <label>Physician</label>
                  <input
                    type="text"
                    value={editPhysician}
                    onChange={(e) => setEditPhysician(e.target.value)}
                    placeholder="Last Name, First Name"
                  />

                  <label>Notes</label>
                  <textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="Optional notes"
                  />
                </div>

              </div>

              {/* TRACKING NUMBERS */}
              {editStatus !== "Cancelled" && (
                <>
                  <label>Tracking Numbers</label>

                  {trackingRows.map((row, index) => (
                    <div key={index} className="tracking-row">
                      <span className="tracking-number-label">{index + 1}.</span>

                      <input
                        className="tracking-input"
                        value={row}
                        onChange={(e) =>
                          updateTracking(index, e.target.value)
                        }
                        placeholder="Tracking URL or number"
                      />

                      <FiX
                        className="remove-tracking-btn"
                        onClick={() => removeTrackingRow(index)}
                      />
                    </div>
                  ))}

                  <button className="add-tracking-btn" onClick={addTrackingRow}>
                    + Add Tracking Number
                  </button>
                </>
              )}

            </div>

            <div className="edit-modal-footer">
              <button
                className="edit-save-btn"
                onClick={saveEdit}
                disabled={saving || saveDisabled}
              >
                {saving ? "Saving…" : "Save"}
              </button>

              <button
                className="edit-cancel-btn"
                onClick={closeEditModal}
                disabled={saving}
              >
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}

      {/* ALPHABET MODAL */}
      {modalOpen && (
        <div className="modal-overlay">
          <div className="clinic-modal">

            <div className="modal-header">
              <span>Clinics starting with "{selectedLetter}"</span>
              <button
                className="modal-close-btn"
                onClick={() => setModalOpen(false)}
              >
                ×
              </button>
            </div>

            <div className="clinic-modal-scroll">
              {letterClinics.length === 0 ? (
                <div className="modal-empty">No clinics found</div>
              ) : (
                letterClinics.map((name, i) => (
                  <div
                    key={i}
                    className="clinic-modal-item"
                    onClick={() => {
                      setClinic(name);
                      setModalOpen(false);
                      setTimeout(search, 50);
                    }}
                  >
                    {name}
                  </div>
                ))
              )}
            </div>

            <div className="modal-footer">
              <button className="modal-close" onClick={() => setModalOpen(false)}>
                Close
              </button>
            </div>

          </div>
        </div>
      )}

      {/* MAIN PAGE */}
      <div className="view-wrapper">

        <h2 className="admin-page-title">Edit Orders</h2>

        <div className="search-card-admin">
          <div className="alphabet-wrapper">
            <div className="alphabet-bar alphabet-inside">
              {LETTERS.map(letter => (
                <button
                  key={letter}
                  className="letter-btn"
                  onClick={() => handleLetterClick(letter)}
                >
                  {letter}
                </button>
              ))}
            </div>
          </div>

          <div className="admin-search-grid">
            <input
              placeholder="Clinic name"
              value={clinic}
              onChange={e => setClinic(e.target.value)}
              onKeyDown={e => e.key === "Enter" && search()}
            />

            <input
              placeholder="Patient name (optional)"
              value={patient}
              onChange={e => setPatient(e.target.value)}
              onKeyDown={e => e.key === "Enter" && search()}
            />

            <button onClick={search} disabled={loading}>
              {loading ? "Searching…" : "Search"}
            </button>
          </div>

          <div className="filters-admin">
            <div>
              <label>Status</label>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option>ALL</option>
                <option>In-Process</option>
                <option>Pending</option>
                <option>Shipped</option>
                <option>Out for Delivery</option>
                <option>Delivered</option>
                <option>Cancelled</option>
              </select>
            </div>

            <div>
              <label>Sort</label>
              <select value={sortOrder} onChange={e => setSortOrder(e.target.value)}>
                <option value="NEWEST">Newest first</option>
                <option value="OLDEST">Oldest first</option>
              </select>
            </div>

            {/* F-04: Physician dropdown - populated from current results */}
            <div>
              <label>Physician</label>
              <select value={physicianFilter} onChange={e => setPhysicianFilter(e.target.value)}>
                <option value="ALL">All Physicians</option>
                {Array.from(
                  new Set(
                    rawResults
                      .map(o => o.physician)
                      .filter(p => p && p.trim() !== "")
                  )
                )
                  .sort((a, b) => a.localeCompare(b))
                  .map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
              </select>
            </div>
          </div>

          {error && <p className="error-admin">{error}</p>}
        </div>

        {/* RESULTS */}
        {hasSearched && !loading && filtered.length === 0 && !error && (
          <p className="no-results">No orders found.</p>
        )}

        <div className="results-grid-admin">
          {paginated.map((o, i) => (
            <div key={i} className="result-card-admin">

              <div className="rc-top">
                <div className="rc-name">{o.name}</div>

                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <span
                    className="rc-status"
                    style={{ background: STATUS_COLORS[o.status] }}
                  >
                    {o.status}
                  </span>

                  <FiEdit2
                    className="delete-icon"
                    onClick={() => openEditModal(o)}
                  />
                </div>
              </div>

              <div className="rc-dates">
                {/* F-02/03: <strong>Shipped:</strong> {display(o.shipDate)} */}
                <strong>Order Received:</strong> {displayDate(o.order_received)}
                {/* F-12: Delivery field removed - <strong>Delivery:</strong> {display(o.deliveryDate)} */}
              </div>

              {/* F-04: Physician display - using rc-notes class for consistent styling */}
              <div className="rc-notes">
                <strong>Physician:</strong> {display(o.physician)}
              </div>

              <div className="rc-notes">
                <strong>Notes:</strong> {display(o.comments)}
              </div>

              {/* Tracking */}
              {o.trackingLinks?.length === 1 && (
                <a
                  href={o.trackingLinks[0]}
                  target="_blank"
                  rel="noreferrer"
                  className="track-admin"
                >
                  Track
                </a>
              )}

              {o.trackingLinks?.length > 1 && (
                <>
                  <button
                    className="track-toggle-admin"
                    onClick={() =>
                      setOpenTracking(prev => ({ ...prev, [i]: !prev[i] }))
                    }
                  >
                    Tracking ({o.trackingLinks.length})
                    <span className={`rc-arrow ${openTracking[i] ? "open" : ""}`}>
                      ▸
                    </span>
                  </button>

                  {openTracking[i] && (
                    <div className="rc-track-list">
                      {o.trackingLinks.map((link, idx) => (
                        <a
                          key={idx}
                          href={link}
                          target="_blank"
                          rel="noreferrer"
                          className="track-admin small"
                        >
                          Track {idx + 1}
                        </a>
                      ))}
                    </div>
                  )}
                </>
              )}

            </div>
          ))}
        </div>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="admin-pagination">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
              First
            </button>

            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
            >
              Prev
            </button>

            <span>
              Page {currentPage} of {totalPages}
            </span>

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
            >
              Next
            </button>

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(totalPages)}
            >
              Last
            </button>
          </div>
        )}

      </div>
    </>
  );
}
